import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Plus, ArrowRight, CaretRight, X, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Tournaments() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", course_id: null, tee_name: "Blue", format: "stableford",
    rounds_count: 2, cut_after_round: "", cut_top_n: "",
    prize_1: "Trophy + grand prize", prize_2: "", prize_3: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tournaments");
    setList(data);
  };
  useEffect(() => {
    load();
    api.get("/courses").then((r) => setCourses(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.course_id) { toast.error("Pick a course"); return; }
    setBusy(true);
    try {
      const prizes = [form.prize_1, form.prize_2, form.prize_3]
        .map((label, i) => label && label.trim() ? { position: i + 1, label: label.trim() } : null)
        .filter(Boolean);
      const body = {
        name: form.name,
        course_id: form.course_id,
        tee_name: form.tee_name,
        format: form.format,
        rounds_count: Number(form.rounds_count),
        prizes,
      };
      if (form.cut_after_round) body.cut_after_round = Number(form.cut_after_round);
      if (form.cut_top_n) body.cut_top_n = Number(form.cut_top_n);
      const { data } = await api.post("/tournaments", body);
      toast.success("Tournament created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const join = async (id) => {
    try {
      await api.post(`/tournaments/${id}/join`);
      toast.success("Joined");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between rise">
        <div>
          <span className="label-eyebrow">Tournaments</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Compete.</h1>
          <p className="text-[#0F2537]/70 mt-2 max-w-xl">Run a multi-round event with cuts and prizes.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-tournament-btn"
          className="px-4 py-2.5 rounded-xl bg-[#E05D3A] text-white font-bold inline-flex items-center gap-2 hover:brightness-110">
          <Plus weight="bold" size={16} /> New
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="card-default p-5 md:p-7 space-y-4" data-testid="new-tournament-form">
          <div className="flex items-center justify-between">
            <span className="label-eyebrow">Set up tournament</span>
            <button type="button" onClick={() => setOpen(false)} className="p-1 text-[#0F2537]/60"><X size={16} /></button>
          </div>
          <div>
            <label className="text-sm font-semibold">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="t-name" className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]"
              placeholder="Bula Open 2026" />
          </div>
          <div>
            <label className="text-sm font-semibold">Course</label>
            <div className="mt-1 grid sm:grid-cols-2 gap-2">
              {courses.map((c) => (
                <button key={c.id} type="button" onClick={() => setForm({ ...form, course_id: c.id })}
                  data-testid={`t-course-${c.id}`}
                  className={`text-left p-3 rounded-xl border ${form.course_id === c.id ? "border-[#0F2537] bg-[#0F2537]/5" : "border-[#E2E0D8]"}`}>
                  <div className="font-bold text-sm">{c.name}</div>
                  <div className="text-xs text-[#0F2537]/60">{c.location}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold">Tee</label>
              <select value={form.tee_name} onChange={(e) => setForm({ ...form, tee_name: e.target.value })}
                data-testid="t-tee" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white">
                {["Championship","Blue","White","Red"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Format</label>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}
                data-testid="t-format" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white">
                <option value="stableford">Stableford</option>
                <option value="stroke">Stroke play</option>
                <option value="fourball_stableford">4-Ball Better Ball Stableford</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Rounds</label>
              <input type="number" min="1" max="8" value={form.rounds_count} onChange={(e) => setForm({ ...form, rounds_count: e.target.value })}
                data-testid="t-rounds" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Cut after round (optional)</label>
              <input type="number" min="1" value={form.cut_after_round} onChange={(e) => setForm({ ...form, cut_after_round: e.target.value })}
                data-testid="t-cut-after" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white" placeholder="e.g. 1" />
            </div>
            <div>
              <label className="text-sm font-semibold">Keep top N after cut</label>
              <input type="number" min="1" value={form.cut_top_n} onChange={(e) => setForm({ ...form, cut_top_n: e.target.value })}
                data-testid="t-cut-top-n" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white" placeholder="e.g. 8" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold">Prizes (1st / 2nd / 3rd)</label>
            <div className="mt-1 grid sm:grid-cols-3 gap-2">
              <input value={form.prize_1} onChange={(e) => setForm({ ...form, prize_1: e.target.value })} data-testid="t-prize-1" className="px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white" placeholder="1st prize" />
              <input value={form.prize_2} onChange={(e) => setForm({ ...form, prize_2: e.target.value })} data-testid="t-prize-2" className="px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white" placeholder="2nd" />
              <input value={form.prize_3} onChange={(e) => setForm({ ...form, prize_3: e.target.value })} data-testid="t-prize-3" className="px-3 py-2.5 rounded-xl border border-[#E2E0D8] bg-white" placeholder="3rd" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="t-create-submit"
            className="w-full py-3 rounded-xl bg-[#E05D3A] text-white font-bold disabled:opacity-60">
            {busy ? "Creating…" : "Create tournament"}
          </button>
        </form>
      )}

      {list.length === 0 ? (
        <div className="card-default p-10 text-center">
          <Trophy size={32} weight="duotone" className="mx-auto text-[#E05D3A]" />
          <p className="mt-3 text-[#0F2537]/70">No tournaments yet. Start one!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {list.map((t) => {
            const joined = t.leaderboard.some((p) => p.user_id === user?.id);
            return (
              <div key={t.id} className="card-default p-5 rise" data-testid={`tournament-card-${t.id}`}>
                <div className="flex items-start gap-3">
                  {t.course_image && <img src={t.course_image} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="label-eyebrow !text-[#E05D3A]">{t.status.replace("_", " ")}</span>
                    </div>
                    <h3 className="font-heading font-bold text-lg">{t.name}</h3>
                    <div className="text-xs text-[#0F2537]/60 mt-0.5 flex items-center gap-1">
                      <MapPin size={11} weight="fill" /> {t.course_name} · {t.tee_name} · {t.format}
                    </div>
                    <div className="text-xs text-[#0F2537]/70 mt-1">
                      {t.rounds_count} rounds · {t.players_count} players
                      {t.cut_after_round ? ` · cut top ${t.cut_top_n} after R${t.cut_after_round}` : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Link to={`/tournaments/${t.id}`} data-testid={`open-tournament-${t.id}`}
                    className="flex-1 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-sm font-bold inline-flex items-center justify-center gap-1">
                    {joined ? "Open" : "View"} <CaretRight size={14} weight="bold" />
                  </Link>
                  {!joined && t.status === "registration" && (
                    <button onClick={() => join(t.id)} data-testid={`join-tournament-${t.id}`}
                      className="px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold inline-flex items-center gap-1">
                      Join <ArrowRight size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
